// ============================================================================
// RATE PLAN HOOKS
// ============================================================================
// React hooks for Rate Plan API operations

import { useState, useEffect, useCallback } from 'react';
import { ratePlanService } from 'api/services/ratePlanService';
import type {
  RatePlan,
  CreateRatePlanRequest,
  UpdateRatePlanRequest,
  RatePlanSearchRequest,
  ID,
} from 'api/types';

/**
 * Hook to fetch all rate plans (paginated)
 */
export const useRatePlans = (params?: RatePlanSearchRequest) => {
  const [data, setData] = useState<RatePlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 0,
    totalItems: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  // Serialize params to avoid infinite re-render when caller passes an object literal.
  const paramsKey = JSON.stringify(params);

  const fetchRatePlans = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await ratePlanService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => {
    fetchRatePlans();
  }, [fetchRatePlans]);

  return {
    data,
    loading,
    error,
    pagination,
    refetch: fetchRatePlans,
  };
};

/**
 * Hook to fetch a single rate plan by ID
 */
export const useRatePlan = (id: ID) => {
  const [data, setData] = useState<RatePlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchRatePlan = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    setError(null);
    try {
      const response = await ratePlanService.getById(id);
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchRatePlan();
  }, [fetchRatePlan]);

  return {
    data,
    loading,
    error,
    refetch: fetchRatePlan,
  };
};

/**
 * Hook to create a new rate plan
 */
export const useCreateRatePlan = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const createRatePlan = async (
    data: CreateRatePlanRequest,
    options?: {
      onSuccess?: (ratePlan: RatePlan) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await ratePlanService.create(data);
      setSuccess(true);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    createRatePlan,
    loading,
    error,
    success,
  };
};

/**
 * Hook to update a rate plan
 */
export const useUpdateRatePlan = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const updateRatePlan = async (
    id: ID,
    data: UpdateRatePlanRequest,
    options?: {
      onSuccess?: (ratePlan: RatePlan) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await ratePlanService.update(id, data);
      setSuccess(true);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    updateRatePlan,
    loading,
    error,
    success,
  };
};

/**
 * Hook to delete a rate plan
 */
export const useDeleteRatePlan = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const deleteRatePlan = async (
    id: ID,
    rowVersion: number,
    options?: {
      onSuccess?: () => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      await ratePlanService.delete(id, rowVersion);
      setSuccess(true);
      options?.onSuccess?.();
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    deleteRatePlan,
    loading,
    error,
    success,
  };
};
